import { getAccessToken, refreshAccessToken, isTokenExpired, getApiUrl } from './auth'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Workers API를 호출하는 클라이언트 래퍼.
 * Authorization 헤더를 자동으로 포함하며, 401 시 토큰 갱신 후 재시도.
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  let token = getAccessToken()

  // 토큰이 만료 임박이면 미리 갱신
  if (token && isTokenExpired(token)) {
    token = await refreshAccessToken()
  }

  const res = await doFetch(path, token, options)

  // 401이면 refresh 시도 후 1회 재시도
  if (res.status === 401) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      const retry = await doFetch(path, newToken, options)
      return handleResponse<T>(retry)
    }
    // refresh 실패 → 로그인 페이지로
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
    throw new ApiError(401, '인증이 만료되었습니다.')
  }

  return handleResponse<T>(res)
}

function doFetch(path: string, token: string | null, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const apiUrl = getApiUrl()
  const url = path.startsWith('http') ? path : `${apiUrl}${path}`

  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  })
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }))
    throw new ApiError(res.status, body.error ?? 'Unknown error')
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}
